import { HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import {
  EdenredBalanceId,
  EdenredRechargeResultState,
  EdenredTenantSlug,
  PendingEdenredRecharge
} from 'src/app/interfaces';
import { HttpService } from 'src/app/services/http.service';
import { PaymentMethodsService } from 'src/app/services/payment-methods.service';
import { savePendingEdenredRecharge } from 'src/app/services/edenred-pending-recharge';
import { environment } from 'src/environments/environment';

/**
 * Fallback per-product limits, used only until `GET /wallets/edenred` answers with the
 * authoritative per-tenant values. Deliberately the conservative end of each range, so the field
 * is never unbounded on first paint.
 */
const EDENRED_FALLBACK_LIMITS: {[slug: string]: {min: number, max?: number}} = {
  [EdenredTenantSlug.TICKET_RESTAURANT]: { min: 1000 },
  [EdenredTenantSlug.JUNAEB]: { min: 200, max: 320000 }
};

/** Error code the API returns when the Edenred link itself has lapsed (see `ErrorCodes`). */
const EDENRED_LINK_EXPIRED_CODE = '401001';

/** Domains Edenred serves Connect from; the environment decides between sandbox and production. */
const EDENRED_AUTHORIZE_DOMAINS = ['.edenred.io', '.edenred.com', '.edenred.net'];

export interface EdenredRechargeModalData {
  balanceId: EdenredBalanceId;
  slug: EdenredTenantSlug;
  storeId?: string;
}

/**
 * Amount entry for an Edenred top-up.
 *
 * Once the Edenred account is linked the recharge completes in a single request with no redirect.
 * Only the first time, or after the refresh token lapses, does the backend answer 409 — at which
 * point the amount is stashed and the browser sent to Edenred.
 */
@Component({
  selector: 'app-edenred-recharge-modal',
  templateUrl: './edenred-recharge-modal.component.html',
  styleUrls: ['./edenred-recharge-modal.component.css']
})
export class EdenredRechargeModalComponent implements OnInit {
  amount: number = 0;
  minimumRechargeAmount: number;
  maximumRechargeAmount: number;
  /** Claimed by `onSend()` so a second tap cannot fire a second charge. */
  rechargeInProgress: boolean = false;
  /** Set once the browser is on its way to Edenred, so the lock is never released here. */
  private leavingToEdenred: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EdenredRechargeModalData,
    private dialogRef: MatDialogRef<EdenredRechargeModalComponent>,
    private snackBar: MatSnackBar,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
    private router: Router,
    private http: HttpService,
    private paymentMethods: PaymentMethodsService,
    @Inject(DOCUMENT) private document: Document
  ) {
    const limits =
      EDENRED_FALLBACK_LIMITS[this.data.slug] ?? { min: environment.minimumRechargeAmount };
    this.minimumRechargeAmount = Math.max(environment.minimumRechargeAmount, limits.min);
    this.maximumRechargeAmount = this.clampMaximum(limits.max);
  }

  ngOnInit(): void {
    this.loadLimits();
  }

  close(): void {
    this.dialogRef.close();
  }

  /** The product name, from the one place display names are defined. */
  get walletName(): string {
    return this.paymentMethods.getBalanceDisplayName(this.data.balanceId);
  }

  /**
   * Replace the fallback bounds with the API's, so the Chilean limits have a single owner.
   * A failure here is not worth surfacing: the fallbacks stand and the backend validates anyway.
   */
  private async loadLimits(): Promise<void> {
    try {
      const response = await this.http.edenredAccounts();
      const account = (response.data ?? []).find(candidate => candidate.slug === this.data.slug);
      if (!account) {
        return;
      }
      this.minimumRechargeAmount = Math.max(
        environment.minimumRechargeAmount,
        account.minAmount
      );
      this.maximumRechargeAmount = this.clampMaximum(account.maxAmount);
    } catch (error) {
      console.warn('Could not load Edenred limits; using the built-in fallbacks', error);
    }
  }

  /**
   * Bound a tenant maximum by the app-wide recharge ceiling. A tenant may allow more than
   * `environment.maximumRechargeAmount`, and taking that unclamped would quietly let one wallet
   * exceed a platform-wide rule.
   */
  private clampMaximum(tenantMaximum?: number | null): number {
    return Math.min(environment.maximumRechargeAmount, tenantMaximum ?? Infinity);
  }

  get amountIsValid(): boolean {
    return this.amount >= this.minimumRechargeAmount && this.amount <= this.maximumRechargeAmount;
  }

  /**
   * Charge the linked Edenred account. The in-flight flag is claimed before the first `await`:
   * `spinner.show()` only paints on the next change-detection pass, leaving a window in which a
   * second tap fires a second charge.
   */
  async onSend(): Promise<void> {
    if (this.rechargeInProgress || !this.amountIsValid) {
      return;
    }
    this.rechargeInProgress = true;
    this.spinner.show();
    try {
      const response = await this.http.edenredRecharge(this.data.slug, { amount: this.amount });
      if (!response.successful || !response.data) {
        this.showError('components.edenred_recharge_modal.error');
        return;
      }
      this.dialogRef.close(true);
      // What the API charged, not what was typed, and carried in the navigation state rather than
      // the query string: an editable URL would make the result page a forgeable receipt.
      const resultState: EdenredRechargeResultState = {
        rechargeId: response.data.rechargeId,
        amount: response.data.amount,
        storeId: this.data.storeId
      };
      this.router.navigate(['/edenred/result'], { state: resultState });
    } catch (error) {
      await this.handleRechargeError(error as HttpErrorResponse);
    } finally {
      // Leave the spinner up and the trigger locked while the browser unloads towards Edenred.
      if (!this.leavingToEdenred) {
        this.spinner.hide();
        this.rechargeInProgress = false;
      }
    }
  }

  /**
   * A 409 means the Edenred account is not linked (or was revoked). Stash the recharge and
   * start the one-off login; everything else is surfaced to the user.
   *
   * A 401 is only treated the same way when the API says the *Edenred link* expired. A bare 401
   * also covers an expired Firebase session, and sending that user off to Edenred SSO would fix
   * nothing while hiding the real problem, so it falls through to the generic error.
   */
  private async handleRechargeError(error: HttpErrorResponse): Promise<void> {
    if (error.status === 409) {
      await this.startEdenredLink();
      return;
    }
    if (error.status === 402) {
      this.showError('components.edenred_recharge_modal.insufficient_funds');
      return;
    }
    if (error.status === 401 && error.error?.code === EDENRED_LINK_EXPIRED_CODE) {
      await this.startEdenredLink();
      return;
    }
    console.error(error);
    this.showError('components.edenred_recharge_modal.error');
  }

  /**
   * Persist the pending recharge and send the browser to Edenred Connect. Only reached from
   * `onSend()`, which owns the spinner and the in-flight lock across this leg too, so neither is
   * touched here beyond flagging the departure.
   */
  private async startEdenredLink(): Promise<void> {
    try {
      const response = await this.http.edenredLinkInit(this.data.slug);
      if (!response.successful || !response.data) {
        this.showError('components.edenred_recharge_modal.error');
        return;
      }
      const authorizeUrl = response.data.authorizeUrl;
      if (!this.isSafeAuthorizeUrl(authorizeUrl)) {
        console.error('Refusing to navigate to an unexpected Edenred authorize URL', authorizeUrl);
        this.showError('components.edenred_recharge_modal.error');
        return;
      }
      const pending: PendingEdenredRecharge = {
        slug: this.data.slug,
        amount: this.amount,
        storeId: this.data.storeId
      };
      savePendingEdenredRecharge(pending);
      this.leavingToEdenred = true;
      // Through the injected document rather than `window`, so tests can stub the navigation
      // instead of reloading the Karma page mid-run.
      this.document.location.href = authorizeUrl;
    } catch (error) {
      console.error(error);
      this.showError('components.edenred_recharge_modal.error');
    }
  }

  /**
   * Whether a URL is safe to hand to `window.location.href`. The value comes from our own API, but
   * assigning any string there is a navigation primitive: a `javascript:` URL executes in this
   * origin, and a wrong host carries the handshake `state` somewhere it does not belong.
   *
   * A suffix match rather than an exact host, because the environment picks between Edenred's
   * sandbox and production deployments. A new Connect domain means updating that list.
   */
  private isSafeAuthorizeUrl(candidate: string): boolean {
    let url: URL;
    try {
      url = new URL(candidate);
    } catch {
      return false;
    }
    if (url.protocol !== 'https:') {
      return false;
    }
    return EDENRED_AUTHORIZE_DOMAINS.some(
      domain => url.hostname === domain.slice(1) || url.hostname.endsWith(domain)
    );
  }

  private showError(translationKey: string): void {
    this.snackBar.open(
      this.translate.instant(translationKey),
      this.translate.instant('common.buttons.close'),
      { duration: 4000 }
    );
  }
}
